// ============================================================================
// deriveRoute — turn an allocation's OperationStage chain into the Nodes and
// Rails the timeline renders. Pure: no React, no fetching, no formatting.
//
// Vocabulary: see procurement_materialdepot/CONTEXT.md (Node / Rail / Route).
// Rules and rejected alternatives: see docs/adr/0001-oms-allocation-timeline.md.
// ============================================================================
import { OperationStage, StageEndpoint } from "../types";

export interface Node {
  kind: string | null;
  ref_id: number | null;
  label: string;
}

export interface Rail {
  from: Node;
  to: Node;
  stages: OperationStage[];
  /** 0..1 — how far along this hop the goods physically are. Never 1 until every stage completes. */
  fill: number;
  /** False when no stage on this Rail books a truck (a vendor-delivered first mile). Render dashed. */
  trackable: boolean;
  /**
   * True when this Rail's origin is not the previous Rail's destination — the route has a gap.
   * Real cause: `_endpoints_for` runs at plan time, so after an express upgrade the surviving
   * `purchase` stage still points at the receiving WH while the new `dropship` goes
   * vendor -> customer. Surfaced rather than hidden, so ops sees the anomaly instead of a
   * timeline that quietly implies the two hops connect.
   */
  disconnectedFromPrevious: boolean;
}

// `LogisticsStatus` (oms/logistics/models/enums.py) as physical progress. Folded from carrier
// webhooks, so unlike the stage checklist it is a real position signal rather than paperwork.
//
// `dispatched` sits near zero on purpose: "OUT_FOR_PICKUP" maps to it, meaning the truck is on
// its way to COLLECT — the goods have not left the origin. Only `in_transit` means they moved.
const LOGISTICS_LADDER: Record<string, number> = {
  created: 0,
  dispatched: 0.05,
  in_transit: 0.55,
  out_for_delivery: 0.85,
  delivered: 1,
  received: 1,
  cancelled: 0,
};

// Stages that never book a truck, so they have no position to read — binary on completion.
const UNTRACKABLE_TYPES = new Set([
  "purchase",
  "pick",
  "package",
  "invoice",
  "supplier_delivery",
  // Work performed at the customer's site (an installation, a site visit) — no goods move, so there
  // is no vehicle and no position to read. Binary on the operator's completion, like a pick.
  "service",
]);

const isTrackable = (stage: OperationStage): boolean =>
  !UNTRACKABLE_TYPES.has(stage.operation_type);

function stageFill(stage: OperationStage): number {
  // Completion is the only route to 1. `runner.is_complete` requires every required Zoho doc
  // step to be DONE as well as the movement, so a delivered truck with an outstanding invoice
  // must stay short of full — that stuck-on-paperwork state is the point of the view.
  if (stage.issue_state === "completed") return 1;
  if (!isTrackable(stage)) return 0;

  const bookings = stage.logistics ?? [];
  const active = bookings.find((b) => b.is_active) ?? bookings[0];
  if (!active) return 0;

  return Math.min(LOGISTICS_LADDER[active.status] ?? 0, 0.9);
}

export interface Route {
  nodes: Node[];
  rails: Rail[];
  /** Reverse stages (`return_receive`) — rendered as bare rows beneath the customer Node. */
  returns: OperationStage[];
  /** Cancelled stages. Off the Route entirely; drives the "all stages cancelled" empty state. */
  cancelled: OperationStage[];
}

const asNode = (e: StageEndpoint): Node => ({
  kind: e.kind,
  ref_id: e.ref_id,
  label: e.label,
});

// Node identity. A null ref_id is DISTINCT AND UNMERGEABLE — a supplier with no warehouse
// row and a customer with no ship address are different places, so they must never compare
// equal. We key the null case on the stage and slot it came from, making it unique.
const identityOf = (
  endpoint: StageEndpoint | null,
  stageId: number,
  slot: "source" | "destination"
): string =>
  endpoint?.ref_id == null
    ? `null:${slot}:${stageId}`
    : `${endpoint.kind}:${endpoint.ref_id}`;

// A stage that starts and ends at the same place (pick / package inside the fulfilment
// warehouse). It is not a hop, so it never opens a Rail — it rides the next one.
const isSelfLoop = (stage: OperationStage): boolean =>
  stage.source?.ref_id != null &&
  stage.source.ref_id === stage.destination?.ref_id &&
  stage.source.kind === stage.destination?.kind;

export function deriveRoute(stages: OperationStage[]): Route {
  const ordered = [...stages].sort((a, b) => a.sequence - b.sequence);

  // The Route describes the path the goods actually take, so only forward, non-cancelled
  // stages feed the derivation. Including either would make the Node walk non-monotonic —
  // a delivered-then-returned allocation would be 100% complete and moving backwards at
  // once — which leaves the progress line undefinable. See docs/adr/0001.
  const returns = ordered.filter((s) => s.direction === "reverse");
  const cancelled = ordered.filter((s) => s.issue_state === "cancelled");
  const forward = ordered.filter(
    (s) => s.direction !== "reverse" && s.issue_state !== "cancelled"
  );

  const rails: Rail[] = [];
  const railKeys: string[] = [];
  let pendingSelfLoops: OperationStage[] = [];

  for (const stage of forward) {
    if (isSelfLoop(stage)) {
      pendingSelfLoops.push(stage);
      continue;
    }
    const fromId = identityOf(stage.source, stage.id, "source");
    const toId = identityOf(stage.destination, stage.id, "destination");
    const key = `${fromId}->${toId}`;

    // Consecutive stages over the same hop are one Rail: the PO and the GRN both run
    // vendor -> receiving WH, and ops reads that as a single physical movement.
    if (rails.length > 0 && railKeys[railKeys.length - 1] === key) {
      rails[rails.length - 1].stages.push(...pendingSelfLoops, stage);
    } else {
      rails.push({
        from: asNode(stage.source as StageEndpoint),
        to: asNode(stage.destination as StageEndpoint),
        stages: [...pendingSelfLoops, stage],
        fill: 0,
        trackable: false,
        disconnectedFromPrevious: false,
      });
      railKeys.push(key);
    }
    pendingSelfLoops = [];
  }

  // A trailing self-loop (a WMS step ending the chain) has nothing downstream to fold
  // forward into, so it rides the Rail that precedes it instead of being dropped.
  if (pendingSelfLoops.length > 0 && rails.length > 0) {
    rails[rails.length - 1].stages.push(...pendingSelfLoops);
  }

  rails.forEach((rail, i) => {
    if (i === 0) return;
    const previousTo = railKeys[i - 1].split("->")[1];
    const thisFrom = railKeys[i].split("->")[0];
    rail.disconnectedFromPrevious = previousTo !== thisFrom;
  });

  for (const rail of rails) {
    rail.fill =
      rail.stages.reduce((sum, s) => sum + stageFill(s), 0) / rail.stages.length;
    rail.trackable = rail.stages.some(isTrackable);
  }

  const nodes: Node[] = [];
  const nodeKeys: string[] = [];
  const pushNode = (node: Node, key: string) => {
    if (nodeKeys[nodeKeys.length - 1] === key) return; // already the node we're standing on
    nodes.push(node);
    nodeKeys.push(key);
  };
  rails.forEach((rail, i) => {
    const [fromId, toId] = railKeys[i].split("->");
    pushNode(rail.from, fromId);
    pushNode(rail.to, toId);
  });

  return { nodes, rails, returns, cancelled };
}
